// Org-scoped runtime knobs the operator can flip without redeploys. The
// dedup layer reads these; future layers (probabilistic matcher, judge
// budgets, etc.) extend the schema rather than introducing their own table.
// Cross-package contract: these schemas are the source of truth.
// (DEV-16226 / DEV-16223.)
import { z } from 'zod';
/**
 * What the Signal Orchestrator does when it decides a run is a duplicate.
 *
 * - `enforce` — cancel the run, link it to the signal, emit
 *   `run.deduplicated` with `enforced: true`. The production default.
 * - `dry-run` — record the decision (event + Run.dedupReason) but DO NOT
 *   cancel the run. Lets operators turn the layer on observation-only first,
 *   verify the keys and matches are right across real traffic, then flip to
 *   `enforce`. Critical for the rollout story.
 */
export const DedupDecisionModeSchema = z.enum(['enforce', 'dry-run']);
/**
 * The persisted org-settings row (one per org, lazily created with defaults
 * on first read). All fields are server-resolved on read — clients never
 * receive `null` for a field that has a built-in default. (DEV-16226 +
 * L2/L3 dedup follow-on.)
 */
export const OrgSettingsSchema = z.object({
    orgId: z.string(),
    /**
     * Org-level kill-switch for the entire dedup layer. When false, the
     * orchestrator ignores this org's run/signal stream entirely — every
     * layer (L1/L2/L3) is off.
     */
    dedupEnabled: z.boolean(),
    dedupMode: DedupDecisionModeSchema,
    /**
     * Skip matching against signals older than this many hours. Null = no
     * age cutoff (every OPEN signal is fair game). Applies to every layer.
     */
    maxSignalAgeHours: z.number().int().positive().nullable(),
    /** Layer 2 (probabilistic / semantic) — independent toggle from L1. */
    dedupL2Enabled: z.boolean(),
    /**
     * Cosine *similarity* threshold in basis points (0..10000); a candidate
     * embedding must be >= this similar to an open signal to count as a
     * match. Default 7800 (= 0.78). Higher = stricter.
     */
    dedupL2ThresholdBp: z.number().int().min(0).max(10000),
    /** Embedding model id; default text-embedding-3-small (1536 dims). */
    dedupL2EmbeddingModel: z.string().min(1),
    /** Layer 3 (LLM-agent judge) — independent toggle from L1/L2. */
    dedupL3Enabled: z.boolean(),
    /** FK to the Agent the operator chose as their L3 dedup judge. */
    dedupL3AgentId: z.string().nullable(),
    /** Daily LLM spend cap on L3 calls (USD cents); default 5000 = $50. */
    dedupL3MaxDailyUsdCents: z.number().int().nonnegative(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * PATCH body — every field optional; omitted fields are left untouched.
 * `maxSignalAgeHours` accepts explicit `null` to clear an age cutoff;
 * `dedupL3AgentId` accepts explicit `null` to unset the L3 judge.
 *
 * Conditional invariant enforced by the service (not Zod, since the rule
 * spans the merged result of the patch + current row): when the patch leaves
 * `dedupL3Enabled` true, `dedupL3AgentId` must be non-null.
 */
/**
 * Console nav feature flags — Formant-internal gating of sections that are
 * built but not customer-ready (2026-07-07 nav review). Stored per-org on
 * OrgSetting.consoleFeatureFlags as JSON; missing keys resolve to false.
 * Managed ONLY by superusers via the admin endpoints / the console's
 * Internal page; every org member's console reads the resolved values to
 * decide which nav sections to render.
 */
export const ConsoleFeatureFlagsSchema = z.object({
    /** Persistent Agent orchestration and self-drive controls. */
    persistentAgentOrchestration: z.boolean(),
    /** Refinement section (investigative refinement sessions). */
    refinement: z.boolean(),
    /** Deduplication settings and analytics (Signal Orchestrator, DEV-16226). */
    deduplication: z.boolean(),
    /** Remote Realtime Tools admin page (MCP realtime source registration). */
    remoteRealtimeTools: z.boolean(),
    /** Platform Costs section — LLM cost, usage, and trace detail (PR #645). */
    platformCosts: z.boolean(),
    /** Built-in "Robot onboarding" persistent agent (`agt_robot_onboarding`). */
    robotOnboarding: z.boolean(),
});
/** Every flag starts off — sections stay hidden until explicitly enabled. */
export const defaultConsoleFeatureFlags = {
    persistentAgentOrchestration: false,
    refinement: false,
    deduplication: false,
    remoteRealtimeTools: false,
    platformCosts: false,
    robotOnboarding: false,
};
/** PATCH body — every flag optional; omitted flags are left untouched. */
export const UpdateConsoleFeatureFlagsInputSchema = ConsoleFeatureFlagsSchema.partial();
export const UpdateOrgSettingsInputSchema = z.object({
    dedupEnabled: z.boolean().optional(),
    dedupMode: DedupDecisionModeSchema.optional(),
    maxSignalAgeHours: z.number().int().positive().nullable().optional(),
    dedupL2Enabled: z.boolean().optional(),
    dedupL2ThresholdBp: z.number().int().min(0).max(10000).optional(),
    dedupL2EmbeddingModel: z.string().min(1).optional(),
    dedupL3Enabled: z.boolean().optional(),
    dedupL3AgentId: z.string().nullable().optional(),
    dedupL3MaxDailyUsdCents: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=org-settings.js.map